/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
import styled from 'styled-components';
import {Collapsible, FontSizes, FontWeights, Text, minWidth} from '@calm-web/design-system';
import {FaqItem} from '../../../homepageRefresh/homepage/FAQ/styles';
function _createMdxContent(props) {
  const _components = Object.assign({
    p: "p",
    ol: "ol",
    li: "li",
    a: "a",
    ul: "ul"
  }, _provideComponents(), props.components);
  return _jsxs(_Fragment, {
    children: [_jsx(FaqItem, {
      $hideBorder: true,
      triggerTextColor: "bannerText",
      trigger: 'How much sleep do I need?',
      children: _jsx(_components.p, {
        children: "While the recommended amount of sleep for adults is generally 7 to 9 hours per night, individual\nrequirements can vary person to person, depending on factors such as age, lifestyle, and genetics. Pay\nattention to your body's signals, strive for consistency in your sleep routine to help regulate your body's\ninternal clock, and prioritize both sleep duration and quality."
      })
    }), "\n", _jsxs(FaqItem, {
      triggerTextColor: "bannerText",
      trigger: 'How do I fall asleep faster?',
      children: [_jsx(_components.p, {
        children: "Finding it challenging to drift off to dreamland? Don't worry; you're not alone. Here are a few things that\nmight help:"
      }), _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Establish a bedtime routine. Creating a calming bedtime routine can signal to your body and mind that it's time to unwind and prepare for sleep. Our ", _jsx(_components.a, {
            href: "/app/class/movement-evening",
            children: "Daily Move nighttime series"
          }), " can help promote relaxation. Calm also offers ", _jsx(_components.a, {
            href: "/app/class/music-mood-wind-down",
            children: "music and soundscapes"
          }), " that can set the stage for a peaceful sleep. What matters most is consistency."]
        }), "\n", _jsxs(_components.li, {
          children: ["Create a soothing nighttime environment. Set the tone for restful sleep by optimizing your surroundings. Ensure your bedroom is cool, dark, and quiet. Consider using earplugs, an eye mask, or one of our ", _jsx(_components.a, {
            href: "/app/class/music-genre-ambient",
            children: "white noise soundscapes"
          }), " to give your brain something relaxing to focus on. This can help block out distractions and create the conditions for perfect sleep."]
        }), "\n", _jsxs(_components.li, {
          children: ["Limit screen time. Aim to limit screen time at least an hour before bed. The blue light emitted by electronic devices can interfere with your body's natural sleep-wake cycle. Instead, decompress with one of our ", _jsx(_components.a, {
            href: "/app/player/2QQEAZ7IsG",
            children: "Sleep Stories"
          }), " narrated by a voice or two you might recognize."]
        }), "\n"]
      })]
    }), "\n", _jsxs(FaqItem, {
      triggerTextColor: "bannerText",
      trigger: 'What is REM sleep?',
      children: [_jsx(_components.p, {
        children: "REM sleep, which stands for Rapid Eye Movement, is a distinct stage of sleep characterized by rapid eye movements, increased brain activity, and vivid dreams. It is one of the four stages of sleep. It typically occurs cyclically throughout the night, with each REM period becoming progressively longer."
      }), _jsx(_components.p, {
        children: "During REM sleep, brain activity resembles that of wakefulness, while our muscles experience temporary immobility, safeguarding us from physically acting out our dreams. This unique combination of heightened brain activity and muscle relaxation creates an ideal environment for profound mental and emotional restoration."
      }), _jsx(_components.p, {
        children: "REM sleep plays a crucial role in several aspects of our well-being. It is associated with memory consolidation, learning, and emotional regulation. This stage of sleep helps process and integrate information, contributing to cognitive function and creativity."
      })]
    }), "\n", _jsxs(FaqItem, {
      triggerTextColor: "bannerText",
      trigger: 'Why can\'t I sleep?',
      children: [_jsx(_components.p, {
        children: "There can be various reasons why you may be experiencing trouble sleeping."
      }), _jsx(_components.p, {
        children: "Here are a few common factors that can contribute to sleep difficulties:"
      }), _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Stress and Anxiety: High levels of stress or anxiety can make it difficult to relax and fall asleep. Racing thoughts, worry, or an overactive mind can keep you awake at night."
        }), "\n", _jsx(_components.li, {
          children: "Poor Sleep Habits: Irregular sleep patterns, inconsistent bedtime routines, excessive caffeine or alcohol consumption, and engaging in stimulating activities before bed can disrupt your sleep."
        }), "\n", _jsx(_components.li, {
          children: "Environmental Factors: Uncomfortable sleeping environment, excessive noise, extreme temperatures, or inadequate lighting can interfere with your ability to fall asleep or stay asleep."
        }), "\n", _jsx(_components.li, {
          children: "Medical Conditions: Certain medical conditions, such as chronic pain, sleep apnea, restless leg syndrome, or hormonal imbalances can disrupt your sleep."
        }), "\n", _jsx(_components.li, {
          children: "Mental Health Issues: Depression, anxiety, bipolar disorder, post-traumatic stress disorder (PTSD), and other mental health challenges can affect sleep patterns and quality."
        }), "\n"]
      }), _jsx(_components.p, {
        children: "If you're consistently having trouble sleeping, it's advisable to consult with a healthcare professional who can help assess your specific situation and provide guidance that's tailored to your needs."
      })]
    }), "\n", _jsxs(FaqItem, {
      triggerTextColor: "bannerText",
      trigger: 'Why do I sleep so much?',
      children: [_jsx(_components.p, {
        children: "Excessive sleep, also known as hypersomnia, can stem from various factors. It's essential to approach this occurrence with curiosity and compassion, recognizing that each individual's sleep needs can differ. Some possible causes might be:"
      }), _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Sleep Debt: If you consistently experience insufficient sleep, your body may be trying to compensate for the accumulated sleep debt, resulting in longer periods of sleep."
        }), "\n", _jsx(_components.li, {
          children: "Underlying Health Conditions: Certain medical conditions, such as sleep disorders (e.g., sleep apnea, narcolepsy), depression, or thyroid issues, can contribute to excessive sleep."
        }), "\n", _jsx(_components.li, {
          children: "Lifestyle Factors: Lifestyle choices, such as irregular sleep patterns, poor sleep hygiene, excessive physical or mental exertion, or high stress levels, can impact sleep duration."
        }), "\n"]
      }), _jsx(_components.p, {
        children: "Listen to your body's signals. If you consistently find yourself sleeping excessively and feeling refreshed upon waking, it may indicate that your body genuinely requires that amount of sleep. However, if excessive sleep leaves you feeling lethargic, groggy, or interferes with daily activities, it may be worth exploring potential underlying causes. If excessive sleep significantly impacts your daily life, it may be beneficial to consult with a healthcare professional."
      })]
    }), "\n", _jsxs(FaqItem, {
      triggerTextColor: "bannerText",
      trigger: 'How can I sleep better?',
      children: [_jsxs(_components.p, {
        children: ["We recommend starting with our Fall Asleep collection in the Calm app, which features our most popular ", _jsx(_components.a, {
          href: "/app/player/2QQEAZ7IsG",
          children: "Sleep Stories"
        }), "®, soundscapes, music, and meditations based on your goals:"]
      }), _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Help falling asleep: natural sleep aids"
        }), "\n", _jsx(_components.li, {
          children: "Getting back to sleep: reactive tools used to soothe you back to sleep"
        }), "\n", _jsx(_components.li, {
          children: "Unwinding before bed: proactive tools to help start a sleep routine, focused on sleep hygiene - this means building habits and an environment for optimal sleep"
        }), "\n"]
      }), _jsxs(_components.p, {
        children: ["You may have already heard of our most popular sleep offering, Sleep Stories®, which are bedtime stories designed to help you drift off to sleep. Many of our narrators are names you may already be familiar with, from artists like ", _jsx(_components.a, {
          href: "/app/player/2QQEAZ7IsG",
          children: "Harry Styles"
        }), " and ", _jsx(_components.a, {
          href: "/app/program/nVKU_KxPlm",
          children: "Michael Bublé"
        }), " to actors and actresses such as ", _jsx(_components.a, {
          href: "/app/player/TvmOdIeBVj",
          children: "Jennifer Garner"
        }), " and ", _jsx(_components.a, {
          href: "/app/feeds/narrator-u141IqAGf",
          children: "Matthew McConaughey"
        }), ". Kids can also enjoy exclusive Sleep Stories® featuring favorite characters such as ", _jsx(_components.a, {
          href: "/app/player/NK30cxIQzy",
          children: "Peppa Pig"
        }), ", ", _jsx(_components.a, {
          href: "/app/player/sjWv03z4KI",
          children: "Minions"
        }), ", ", _jsx(_components.a, {
          href: "/app/program/SjF_l5VVyT",
          children: "Winnie the Pooh"
        }), ", ", _jsx(_components.a, {
          href: "/app/player/YHgB8hl8yN",
          children: "Transformers"
        }), ", and more."]
      }), _jsxs(_components.p, {
        children: ["We also have soothing soundscapes and music that have been sonically-engineered to help you sleep. Choose from nature sounds such as ", _jsx(_components.a, {
          href: "/app/player/1nLymYtw9L",
          children: "rainstorms"
        }), " and ", _jsx(_components.a, {
          href: "/app/player/D7lklLoUJ",
          children: "ocean waves"
        }), ", to different frequency pitches including ", _jsx(_components.a, {
          href: "/app/player/61i9y0yxqI",
          children: "white"
        }), ", ", _jsx(_components.a, {
          href: "/app/player/cqa16VacfZ",
          children: "brown"
        }), ", and ", _jsx(_components.a, {
          href: "/app/player/VI5Swv6gaz",
          children: "green"
        }), " noise. If you're looking for something that provides a bit more guidance, there's a variety of ", _jsx(_components.a, {
          href: "/app/sleep/meditations",
          children: "sleep meditations"
        }), " and ", _jsx(_components.a, {
          href: "/breathe",
          children: "breathwork"
        }), ". These exercises help lower the heart rate, which shifts your nervous system out of fight or flight and into a relaxed parasympathetic mode, enabling your body to fall into a deeper sleep."]
      }), _jsx(_components.p, {
        children: "Lastly, we also have a Sleep Check-In feature which offers a new way to track your sleep. Check in with your nightly sleep quality, and we'll show you patterns to guide you towards better sleep habits and health."
      })]
    })]
  });
}
function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = Object.assign({}, _provideComponents(), props.components);
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  })) : _createMdxContent(props);
}
export default MDXContent;
